import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { CONTACT, SERVICES } from "@/data/content";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/about", label: "About", testid: "nav-link-about" },
  { to: "/services", label: "Services", testid: "nav-link-services" },
  { to: "/team", label: "Meet the Team", testid: "nav-link-team" },
  { to: "/portfolio", label: "Portfolio", testid: "nav-link-portfolio" },
  { to: "/contact", label: "Contact", testid: "nav-link-contact" },
];

const Nav = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 bg-[#0A0A0B]/80 backdrop-blur-md border-b border-zinc-800/80">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-4 shrink-0">
            <img src="/assets/fpm-logo-white.png" alt="Fox Project Management" className="h-11 w-auto" />
            <img src="/assets/fpm-wordmark-white.png" alt="" aria-hidden="true" className="hidden sm:block h-4 w-auto opacity-90" />
          </Link>
          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                    isActive ? "text-zinc-50" : "text-zinc-500 hover:text-zinc-200"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <Link
              to="/contact"
              data-testid="nav-cta-button"
              className="hidden sm:inline-flex items-center gap-2 border border-zinc-700 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-200 hover:bg-zinc-50 hover:text-zinc-950 hover:border-zinc-50 transition-colors duration-300"
            >
              Start a project <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setOpen(true)}
              className="lg:hidden text-zinc-200 p-2"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] bg-[#0A0A0B]/97 backdrop-blur-xl flex flex-col"
            data-testid="nav-mobile-menu"
          >
            <div className="max-w-7xl mx-auto px-6 h-20 w-full flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img src="/assets/fpm-logo-white.png" alt="Fox Project Management" className="h-11 w-auto" />
                <img src="/assets/fpm-wordmark-white.png" alt="" aria-hidden="true" className="h-4 w-auto opacity-90" />
              </div>
              <button
                data-testid="nav-mobile-close-button"
                onClick={() => setOpen(false)}
                className="text-zinc-200 p-2"
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-6 max-w-7xl mx-auto w-full">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-${l.testid}`}
                    className={({ isActive }) =>
                      `block py-4 border-b border-zinc-800/60 font-display text-3xl font-bold uppercase tracking-tight transition-colors ${
                        isActive ? "text-[#C5A059]" : "text-zinc-200 hover:text-zinc-50"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="px-6 pb-10 max-w-7xl mx-auto w-full font-mono text-xs text-zinc-500 tracking-wider">
              {CONTACT.emailInfo} · {CONTACT.phone}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const Footer = () => (
  <footer className="border-t border-zinc-800/60 bg-void-2/40" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-6 py-16 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
      <div>
        <img src="/assets/fpm-wordmark-white.png" alt="Fox Project Management" className="h-9 w-auto mb-6" />
        <p className="text-sm text-zinc-500 font-light leading-relaxed max-w-xs">
          Smart. Agile. Adaptive. Construction project management consultancy — helping clients deliver with confidence
          and certainty across the UK &amp; Ireland.
        </p>
      </div>
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-5">Explore</p>
        <ul className="space-y-3">
          {LINKS.map((l) => (
            <li key={l.to}>
              <Link
                to={l.to}
                data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm text-zinc-400 hover:text-zinc-50 transition-colors duration-300"
              >
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-5">Services</p>
        <ul className="space-y-3">
          {SERVICES.map((s) => (
            <li key={s.id}>
              <Link
                to="/services"
                className="text-sm text-zinc-400 hover:text-zinc-50 transition-colors duration-300"
              >
                {s.title}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-5">Contact</p>
        <ul className="space-y-3 text-sm text-zinc-400">
          <li>
            <a href={`mailto:${CONTACT.emailInfo}`} className="hover:text-zinc-50 transition-colors duration-300">
              {CONTACT.emailInfo}
            </a>
          </li>
          <li>
            <a href={CONTACT.phoneHref} className="hover:text-zinc-50 transition-colors duration-300">
              {CONTACT.phone}
            </a>
          </li>
          <li className="text-zinc-500">Projects across the UK &amp; Ireland</li>
        </ul>
      </div>
    </div>
    <div className="border-t border-zinc-800/60">
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-3 font-mono text-[11px] tracking-wider text-zinc-600">
        <div className="flex flex-col sm:flex-row justify-between gap-3">
          <span>© 2026 Fox Project Management Consultancy Ltd. All rights reserved.</span>
          <span className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Link to="/privacy-policy" data-testid="footer-link-privacy-policy" className="hover:text-zinc-300 transition-colors duration-300">
              Privacy Policy
            </Link>
            <Link to="/legal" data-testid="footer-link-legal" className="hover:text-zinc-300 transition-colors duration-300">
              Legal
            </Link>
          </span>
        </div>
        <p className="text-zinc-700 leading-relaxed" data-testid="footer-company-info">
          Fox Project Management Consultancy Ltd · Registered office: Westgate House, Seedhill, Paisley, PA1 1JE ·
          Registered in Scotland SC900643
        </p>
      </div>
    </div>
  </footer>
);

const Layout = () => {
  const lenisRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    lenisRef.current?.scrollTo(0, { immediate: true, force: true });
  }, [pathname]);

  return (
    <div className="min-h-screen bg-void text-zinc-100">
      <div className="noise-overlay" />
      <Nav />
      <main key={pathname}>
        <Outlet />
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
